import * as React from 'react';
import { ListItemText, Collapse, ListItemIcon } from '@mui/material';
import {
    HomeOutlined,
    LayersOutlined,
    Diversity3Outlined,
    AdminPanelSettingsOutlined,
    BuildOutlined,
    LogoutOutlined,
    ExpandLess,
    ExpandMore,
    ArrowBackOutlined,
    DashboardOutlined,
    SettingsOutlined,
    ArticleOutlined,
    CloudUploadOutlined,
    DataObjectOutlined,
    FolderOutlined,
} from '@mui/icons-material';
import { Home, Logs, LoginSmart as Login, NotFound, PrimeLendingWebsites, GenericWebsites, DynamicWebsites, SkeletonBootstrap, Users } from './scenes'
import ResponsiveDrawer from './components/ResponsiveDrawer';
import { Link, Route, Routes, RouterProvider, createBrowserRouter, useLocation } from "react-router-dom";
import { serviceLocator } from './services';
import { SmartFileManagerDialog } from './components/Websites/SmartFileManagerDialog';
import { Quality } from './scenes/QualityReports';
import { InfraCredentials } from './scenes/InfraCredentials/InfraCredentials';
import { Agencies } from './scenes/Agencies/Agencies';
import { QualityConfigs } from './scenes/QualityCheckConfig/QualityCheckConfig';
import { Database } from './scenes/Database';
import { Tools } from './scenes/Tools';
import { AgencyPanel } from './scenes/AgencyPanel/Index';

import ListItemButton from "@mui/material/ListItemButton";


const authenticationService = serviceLocator.getAuthenticationService();
const uiService = serviceLocator.getUIService();

const subItemSx = { pl: 5.25 } as const;

const sectionIconSx = { color: 'rgba(244, 246, 255, 0.55)', minWidth: 32 } as const;
const sectionChevronSx = { color: 'rgba(244, 246, 255, 0.42)', fontSize: 18 } as const;

const WEBSITE_TEMPLATES: { [key: string]: string } = {
  'custom-website': 'Custom Website',
  'skeleton-bootstrap-5-2': 'Bootstrap 5.2',
  'simple-business': 'Simple Business',
  'prime-lending': 'Prime Lending',
};

interface WebsiteContext {
  type: string;
  templateLabel: string;
  siteKey: string;
  basePath: string;
}

function detectWebsiteContext(pathname: string): WebsiteContext | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length < 3 || parts[0] !== 'websites') return null;
  const type = parts[1];
  const templateLabel = WEBSITE_TEMPLATES[type];
  if (!templateLabel) return null;
  const siteKey = parts[2];
  if (!siteKey) return null;
  return {
    type,
    templateLabel,
    siteKey,
    basePath: `/websites/${type}/${siteKey}`,
  };
}

interface ExternalMenuItemProps {
  href: string;
  text: string;
  icon?: React.ReactNode;
}

function ExternalMenuItem({ href, text, icon }: ExternalMenuItemProps) {
  return (
    <ListItemButton
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer">
      {icon && <ListItemIcon sx={sectionIconSx}>{icon}</ListItemIcon>}
      <ListItemText primary={text} />
    </ListItemButton>
  );
}

interface ActionMenuItemProps {
  onClick: () => void;
  text: string;
  icon?: React.ReactNode;
}

function ActionMenuItem({ onClick, text, icon }: ActionMenuItemProps) {
  return (
    <ListItemButton onClick={onClick}>
      {icon && <ListItemIcon sx={sectionIconSx}>{icon}</ListItemIcon>}
      <ListItemText primary={text} />
    </ListItemButton>
  );
}

interface SectionToggleProps {
  section: string;
  label: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: (section: string) => (e: React.SyntheticEvent) => void;
}

function SectionToggle({ section, label, icon, expanded, onToggle }: SectionToggleProps) {
  return (
    <ListItemButton onClick={onToggle(section)}>
      <ListItemIcon sx={sectionIconSx}>{icon}</ListItemIcon>
      <ListItemText primary={label} />
      {expanded ? <ExpandLess sx={sectionChevronSx} /> : <ExpandMore sx={sectionChevronSx} />}
    </ListItemButton>
  );
}

function AppContent() {
  const location = useLocation();
  const websiteContext = detectWebsiteContext(location.pathname);
  const [expandedSection, setExpandedSection] = React.useState('');
  const [staticDialogOpen, setStaticDialogOpen] = React.useState(false);
  const [, forceTick] = React.useReducer((x: number) => x + 1, 0);
  const linkComponentsRef = React.useRef<{ [path: string]: any }>({});

  // Close the static-files dialog automatically when the user navigates
  // away from a website context (e.g. clicks "Back to Custom Website").
  React.useEffect(() => {
    if (websiteContext == null && staticDialogOpen) {
      setStaticDialogOpen(false);
    }
  }, [websiteContext, staticDialogOpen]);

  React.useEffect(() => {
    uiService.setForceUpdateHandler(() => forceTick());
  }, []);

  React.useEffect(() => {
    const authService = serviceLocator.getAuthenticationService();
    authService.setUnathorizedHandler(() => {
      authService.signOut();
      window.location.assign('/');
    });
    authService.setForbiddenHandler(() => {});
    return () => {
      authService.setUnathorizedHandler(null);
      authService.setForbiddenHandler(null);
    };
  }, []);

  const handleSectionClick = (section: string) => (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setExpandedSection(prev => prev === section ? '' : section);
  };

  const handleSignOut = () => {
    authenticationService.signOut();
    forceTick();
  };

  const MenuListItem = (props: { to: string; text: string; section?: string; sub?: boolean; icon?: React.ReactNode; activeWhen?: (pathname: string) => boolean }) => {
    let linkComponent = linkComponentsRef.current[props.to];
    if (linkComponent == null) {
      linkComponent = React.forwardRef((p: any, ref: any) => (<Link to={props.to} {...p} ref={ref} />));
      linkComponentsRef.current[props.to] = linkComponent;
    }
    const active = props.activeWhen
      ? props.activeWhen(location.pathname)
      : (props.section != null && props.section === uiService.getActiveSection());
    return (
      <ListItemButton
        sx={props.sub ? subItemSx : undefined}
        selected={active}
        component={linkComponent as any}>
        {props.icon && <ListItemIcon sx={sectionIconSx}>{props.icon}</ListItemIcon>}
        <ListItemText primary={props.text} />
      </ListItemButton>
    );
  };

  const authUserData = authenticationService.getAuthenticatedUserData();

  if (authUserData == null) {
    return <Login />;
  }

  const selectedAgency = uiService.getSelectedAgency() || 'all';
  const selectedAgencyData = authUserData.containedAgencies ? authUserData.containedAgencies.find(item => item.identifier == selectedAgency) : false;
  const isAgent = selectedAgencyData ? selectedAgencyData.users.some(item => item.AgencyUser.role == 'agent') : false;
  const isAdmin = authUserData.roles.indexOf('admin') !== -1;
  const hasAgency = authUserData.ownedAgency == selectedAgency;

  const globalNav = (<React.Fragment>
    <MenuListItem to={'/'} text="Home" section="home" icon={<HomeOutlined fontSize="small" />} />

    <SectionToggle section="websites" label="Websites" icon={<LayersOutlined fontSize="small" />} expanded={expandedSection === 'websites'} onToggle={handleSectionClick} />
    <Collapse in={expandedSection === 'websites'}>
      <MenuListItem to={'/websites/custom-website/'} sub={true} text="Custom Website" section="websites/custom-website" />
      <MenuListItem to={'/websites/skeleton-bootstrap-5-2/'} sub={true} text="Bootstrap 5.2" section="websites/skeleton-bootstrap-5-2" />
    </Collapse>

    {
      (isAdmin || isAgent || hasAgency) &&
      <>
        <SectionToggle section="themePanel" label="Agency Panel" icon={<Diversity3Outlined fontSize="small" />} expanded={expandedSection === 'themePanel'} onToggle={handleSectionClick} />
        <Collapse in={expandedSection === 'themePanel'}>
          <MenuListItem to={'/agency-panel/general/'} sub={true} text="General" section="agency-panel/general" />
          <MenuListItem to={'/agency-panel/members/'} sub={true} text="Members" section="agency-panel/members" />
          <MenuListItem to={'/agency-panel/footer/'} sub={true} text="Global Footer" section="agency-panel/footer" />
        </Collapse>
      </>
    }

    {(isAdmin || hasAgency) && (<React.Fragment>
      <SectionToggle section="admin" label="Administration" icon={<AdminPanelSettingsOutlined fontSize="small" />} expanded={expandedSection === 'admin'} onToggle={handleSectionClick} />
      <Collapse in={expandedSection === 'admin'}>
        {isAdmin && (<MenuListItem to={'/agencies/'} text="Agencies" sub={true} section="agencies" />)}
        {isAdmin && (<MenuListItem to={'/database/'} text="Database" sub={true} section="database" />)}
        {(isAdmin || hasAgency) && (<MenuListItem to={'/infra-credentials/'} text="Infrastructure Credentials" sub={true} section="infra-credentials" />)}
        {isAdmin && <MenuListItem to={'/logs/'} text="Logs" section="logs" sub={true} />}
        {isAdmin && <MenuListItem to={'/quality/'} text="Quality Reports" section="quality-reports" sub={true} />}
        {(isAdmin || hasAgency) && <MenuListItem to={'/quality-check-configs/'} text="Quality Check Config" section="quality-reports-configs" sub={true} />}
        {isAdmin && (<MenuListItem to={'/users/'} text="Users" sub={true} section="users" />)}
      </Collapse>
    </React.Fragment>)}

    <SectionToggle section="tools" label="Tools & Docs" icon={<BuildOutlined fontSize="small" />} expanded={expandedSection === 'tools'} onToggle={handleSectionClick} />
    <Collapse in={expandedSection === 'tools'}>
      <MenuListItem to={'/tools/documentation/'} sub={true} text="Documentation" section="tools/documentation" />
      <MenuListItem to={'/tools/wizards/'} sub={true} text="Wizards" section="tools/wizards" />
      <MenuListItem to={'/tools/safe-url-gen/'} sub={true} text="Safe URL Gen" section="tools/safe-url-gen" />
    </Collapse>

    <ListItemButton onClick={handleSignOut} sx={{ mt: 1 }}>
      <ListItemIcon sx={sectionIconSx}><LogoutOutlined fontSize="small" /></ListItemIcon>
      <ListItemText primary={"Sign Out"} />
    </ListItemButton>
  </React.Fragment>);

  const websiteNav = websiteContext && (<React.Fragment>
    <MenuListItem
      to={`/websites/${websiteContext.type}/`}
      text={`Back to ${websiteContext.templateLabel}`}
      icon={<ArrowBackOutlined fontSize="small" />}
    />
    <MenuListItem
      to={websiteContext.basePath}
      text="Overview"
      icon={<DashboardOutlined fontSize="small" />}
      activeWhen={(p) => p === websiteContext.basePath || p === `${websiteContext.basePath}/`}
    />
    <MenuListItem
      to={`${websiteContext.basePath}/main-form/`}
      text="Site Config"
      icon={<SettingsOutlined fontSize="small" />}
      activeWhen={(p) => p.startsWith(`${websiteContext.basePath}/main-form`)}
    />
    <MenuListItem
      to={`${websiteContext.basePath}/home/`}
      text="Home Page"
      icon={<HomeOutlined fontSize="small" />}
      activeWhen={(p) => p.startsWith(`${websiteContext.basePath}/home`)}
    />
    <MenuListItem
      to={`${websiteContext.basePath}/`}
      text="Pages & Blogs"
      icon={<ArticleOutlined fontSize="small" />}
      activeWhen={(p) => p.startsWith(`${websiteContext.basePath}/collection`)}
    />
    <ActionMenuItem
      onClick={() => setStaticDialogOpen(true)}
      text="Static Files"
      icon={<FolderOutlined fontSize="small" />}
    />
    <MenuListItem
      to={`${websiteContext.basePath}/deploy/`}
      text="Deploy"
      icon={<CloudUploadOutlined fontSize="small" />}
      activeWhen={(p) => p.startsWith(`${websiteContext.basePath}/deploy`)}
    />
    <ExternalMenuItem
      href={`https://${websiteContext.type}--${websiteContext.siteKey}.hugosites.webdrvn.com`}
      text="Open Preview"
      icon={<DataObjectOutlined fontSize="small" />}
    />

    <ListItemButton onClick={handleSignOut} sx={{ mt: 1 }}>
      <ListItemIcon sx={sectionIconSx}><LogoutOutlined fontSize="small" /></ListItemIcon>
      <ListItemText primary={"Sign Out"} />
    </ListItemButton>
  </React.Fragment>);

  return (
    <React.Fragment>
      <ResponsiveDrawer
        key={`agency:${uiService.getSelectedAgency()}`}
        listItems={websiteContext ? websiteNav : globalNav}
        content={<React.Fragment>
          <Routes>
            <Route path="/database/*" element={<Database />} />
            <Route path="/websites/prime-lending/*" element={<PrimeLendingWebsites />} />
            <Route path="/websites/custom-website/*" element={<GenericWebsites />} />
            <Route path="/websites/simple-business/*" element={<DynamicWebsites />} />
            <Route path="/websites/skeleton-bootstrap-5-2/*" element={<SkeletonBootstrap />} />
            <Route path="/quality-check-configs/*" element={<QualityConfigs />} />
            <Route path="/quality" element={<Quality />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/users/*" element={<Users />} />
            <Route path="/infra-credentials/*" element={<InfraCredentials />} />
            <Route path="/agencies/*" element={<Agencies />} />
            <Route path="/tools/*" element={<Tools />} />
            <Route path="/agency-panel/*" element={<AgencyPanel />} />
            <Route path="/" element={<Home />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </React.Fragment>
        }
      />
      {websiteContext && (
        <SmartFileManagerDialog
          assets={false}
          themeKey={websiteContext.type}
          websiteKey={websiteContext.siteKey}
          open={staticDialogOpen}
          onClose={() => setStaticDialogOpen(false)}
          onPick={null}
        />
      )}
    </React.Fragment>
  );
}

const router = createBrowserRouter([
  { path: '*', element: <AppContent /> }
]);

export function AppSmart() {
  return <RouterProvider router={router} />;
}
